import React, {useEffect, useMemo} from "react";
import dynamic from 'next/dynamic';
import PageDescription from "../components/baseComponents/head/pageDescription/PageDescription";
import {addMainHeader, getAnalytics, getContentPage, getHeader} from "../api/app";
import {useRouter} from "next/router";
import {createMetaData} from "../utils/data/createMetaData";
import {useDispatch} from "react-redux";
import panel, {usePanel} from "../redux/reducer/panel";
import {createNewsDate} from "../utils/data/createData";
import {CSSTransition, SwitchTransition} from "react-transition-group";
import Head from "next/head";
import {NEXT_PUBLIC_BASE_URL} from "../constants/copyright";
import getMicrodata from "../utils/data/getMicrodata";

const Project = dynamic(() => import('../components/project/Project'));
const Faq        = dynamic(() => import('../components/faq/Faq'));
const Quiz       = dynamic(() => import('../components/quiz/Quiz'));
const AuthorInfo = dynamic(() => import('../components/authorInfo/AuthorInfo'));
const Horoscope  = dynamic(() => import('../components/horoscope/Horoscope'));
const News = dynamic(() => import('../components/news/News'));


export default function NewsPage({contentPage, type, identifier}) {
  const router = useRouter()
  const dispatch = useDispatch()
  const {receivedData, viewsCount} = usePanel();
  // const {addModal} = useModal();


  let {
    title, created_at, views_count, preview, preview_mobile, content, headings, meta_description, meta_title,
    share_image, likeCount, recommendations, author, bets, dateModified, datePublished, superBets, readings
  } = contentPage.data.content;

  //console.log("contentPage.data.content", contentPage.data.content)

  views_count = viewsCount ?? views_count;

  const recommendationList = createNewsDate(recommendations)
  const {authors} = contentPage?.authors || {}
  const {totalCount} = contentPage.comments['_meta']
  const canonicalUrl = `${NEXT_PUBLIC_BASE_URL}${router.asPath.split('?')[0]}`;

  const tags = useMemo(() => [
    headings[0] && {
      type: 'tag',
      value: headings[0].title,
      color: headings && headings[0]?.color || "#B58F25",
      href: headings[0].href
    },
    {type: "date", value: created_at},
    {type: "time", value: content.time},
    {type: "views", value: views_count},
    {type: "likes", value: likeCount,},
    {type: "comments", value: totalCount}
  ], [created_at, content, headings, views_count, likeCount, totalCount])

  const authorTags = useMemo(() => [
    // {type: "date", value: created_at},
    // {type: "time", value: content.time},
    {type: "views", value: views_count},
    {type: "likes", value: likeCount},
    {type: "comments", value: totalCount}
  ], [created_at, content, headings])


  const tagsTest = useMemo(() => [
    {type: "views", value: views_count},
    {type: "likes", value: likeCount},
  ], [created_at, content, headings])


  const projectTags = useMemo(() => [
    {type: 'tag', value: 'Спецпроект', color: "#FFCD17"},
    {type: "date", value: created_at},
    {type: "time", value: content.time},
    {type: "views", value: views_count},
    {type: "likes", value: likeCount},
    {type: "comments", value: totalCount}
  ], [created_at, content, headings])

  const extraMeta = useMemo(() => ({
    type: type || 'article',
    section: headings[0]?.title || null,
    dateModified: dateModified ? new Date(dateModified * 1000).toISOString() : null,
    datePublished: datePublished ? new Date(datePublished * 1000).toISOString() : null,
    author: author ? `${author.first_name} ${author.last_name}` : null
  }), [headings, type, dateModified, datePublished, author])

  useEffect(() => {
    dispatch(panel.thunks.addView({type, identifier}))
  }, []);

  if (router.isFallback) {
    return <div>Loading...</div>
  }

  const component = {
    podcast: () => (
      <Project {...contentPage.data.content}
               tags={projectTags}
               comments={contentPage.comments}
               recommendation={recommendationList}/>
    ),
    cards: () => (
      <Faq
        {...contentPage.data.content}
        {...{title, tags, preview, headings, author}}
        text={content.text}
        comments={contentPage.comments}
        recommendation={recommendationList}
      />
    ),
    quiz: () => (
      <Quiz {...contentPage.data.content}
            {...{title, preview, headings}}
            tags={tagsTest}
            recommendation={recommendationList}
      />
    ),
    news: () => (
      <News {...{title, tags, preview, preview_mobile, headings, author}}
            text={content.text}
            comments={contentPage.comments}
            recommendation={recommendationList}
            bets={bets}
            superBets={superBets}
            authors={authors}
            readings={readings}
      />
    ),
    author: () => (
      <AuthorInfo
        {...{tags, author}}
        tags={authorTags}
        recommendation={recommendationList}
      />
    ),
    horoscope: () => (
      <Horoscope
        {...contentPage.date}
        className={"horoscope_inner"}
      />
    ),
    default: () => (
      <News {...{title, tags, preview, preview_mobile, headings, author}}
            text={content.text}
            comments={contentPage.comments}
            recommendation={recommendationList}
            bets={bets}
            superBets={superBets}
            authors={authors}
            readings={readings}
      />

    )
  };

  return (
    <>
      <Head>
        <link rel="canonical" href={canonicalUrl}/>
        <meta name={'menuCategory'} content={headings[0].href.slice(1)}/>
        <script type="application/ld+json"
                dangerouslySetInnerHTML={{__html: JSON.stringify(getMicrodata(contentPage.data))}}/>
      </Head>
      <div className="container">
        <PageDescription {...{
          ...createMetaData(meta_title, meta_description, share_image, null, null, canonicalUrl, type),
          extraMeta
        }}/>
        <SwitchTransition>
          <CSSTransition key={`${type}-${router.asPath}`} timeout={{enter: 500, exit: 300}} classNames={"content-page"}>
            {(component[type] || component.default)()}
          </CSSTransition>
        </SwitchTransition>

      </div>
    </>
  );
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking'
  }
}

export async function getStaticProps({ params }) {
  if (!params?.aId || params.aId.length < 2) {
    return { notFound: true };
  }

  const [type, identifier] = params.aId;

  try {
    const [contentPage, analytic, header] = await Promise.all([
      getContentPage({ type, identifier }),
      getAnalytics(),
      getHeader()
    ]);

    if (!contentPage) {
      return { notFound: true };
    }

    return {
      props: {
        contentPage,
        analytic,
        data: { header, contentPage },
        type,
        identifier,
      },
      revalidate: 10,
    };
  } catch (error) {
    const status = error?.data?.status || error.code || error.statusCode;
    if(status === 404) {
      return { notFound: true };
    } else {
      console.error('[...aId] FETCH ERROR', error);
      throw error;
    }
  }
}
